import { useState, useEffect } from "react"

import { capitalize, cloneDeep } from "lodash"
import { Trash, ChevronsLeftRight, ChevronsUpDown, SquareSquare, Settings2, TextCursorInput, MessageCircleQuestion } from "lucide-react"
import { Input, Separator, DefaultTooltip, Button, Slider, ToggleOptionGroup, ToggleOptionGroupItem, Select } from "twyne-new.ui"

import { TRANSFORM_CONFIG } from "."

export default function DefaultTransform({ transform, onChange }) {
  const handleDelete = index => {
    const newTransform = cloneDeep(transform)
    newTransform.splice(index, 1)
    onChange(newTransform)
  }

  const handleChange = (index, value, key) => {
    const newTransform = cloneDeep(transform)
    newTransform[index][key] = value
    onChange(newTransform)
  }

  return <>
    <DefaultItems
      transform={transform}
      onChange={handleChange}
      onDelete={handleDelete}
    />
  </>
}

const DefaultItems = ({ transform, onChange, onDelete }) => <>
  {transform?.map((transform, index) => {
    const Component = transform.name === 'arbitrary' ? ArbitraryItem : TransformItem

    return <>
      <Component
        key={index}
        index={index}
        config={TRANSFORM_CONFIG.find(item => item.value === transform.name)?.config}
        onChange={onChange}
        transform={transform}
        onDelete={onDelete}
      />
      <Separator className='m-0 my-2' />
    </>
  })}
</>

const ArbitraryItem = ({ index, onChange, onDelete, transform: { value, name } }) => <>
  <div className='flex flex-col gap-2'>
    <div className='flex gap-1 items-center'>
      {capitalize(name)}
      <DefaultTooltip contentProps={{ className: 'max-w-52' }} content='Type any valid CSS transform function (e.g. rotate(45deg), scale(1.2,0.8), skew(10deg,5deg), matrix(...)).'>
        <MessageCircleQuestion size='12' />
      </DefaultTooltip>
    </div>
    <div className='flex gap-2 items-center'>
      <Input
        value={value}
        onChange={e => onChange(index, e.target.value, 'value')}
        placeholder='rotate(45deg)'
      />
      <DefaultTooltip side='right' content='Delete this transform' triggerProps={{ asChild: true }}>
        <Button className='w-10' variant='light' color='danger' onClick={() => onDelete(index)}>
          <Trash size='14' />
        </Button>
      </DefaultTooltip>
    </div>
  </div>
</>

const TransformItem = ({ index, config, onChange, onDelete, transform, transform: { value, type, name } }) => {
  const [predefinedInput, setPredefinedInput] = useState()

  const InputRender = predefinedInput ? SliderInput : ArbitraryInput

  useEffect(() => {
    if (predefinedInput === undefined) {
      setPredefinedInput(value > config.max || value < config.min || value.split('/').length > 1 ? false : true)
    }
  }, [predefinedInput, value, config.max, config.min])

  const handleToggleInput = () => {
    setPredefinedInput(!predefinedInput)
    if (value.split('/').length > 1) {
      onChange(index, value.split('/')[0], 'value')
    }
  }

  return <>
    <div className='flex flex-col gap-2'>
      {`${capitalize(name)} ${type === 'all' ? '' : capitalize(type)}`}
      <div className='flex gap-2 items-center'>
        <Toggles type={type} onChange={onChange} index={index} config={config} />
        <DefaultTooltip side='right' content='Delete this transform' triggerProps={{ asChild: true }}>
          <Button className='w-10' variant='light' color='danger' onClick={() => onDelete(index)}>
            <Trash size='14' />
          </Button>
        </DefaultTooltip>
      </div>

      <div className='flex gap-2 items-center'>
        <InputRender
          value={value}
          onChange={value => onChange(index, String(value), 'value')}
          {...config}
          {...transform}
        />
        {config?.customInput && <config.customInput type={type} value={value} index={index} onChange={onChange} />}
        <DefaultTooltip side='right' content={`${predefinedInput ? 'Show arbitrary input' : 'Show slider'}`} triggerProps={{ asChild: true }}>
          <Button className='w-10' variant='light' color='accent' onClick={handleToggleInput}>
            {predefinedInput ? <TextCursorInput size='14' /> : <Settings2 size='14' />}
          </Button>
        </DefaultTooltip>
      </div>
    </div>
  </>
}

const ArbitraryInput = ({ value, onChange, units = [], defaultUnit = '' }) => {
  const [unit, setUnit] = useState()

  useEffect(() => {
    if (unit === undefined) {
      const unitSplit = value.split('/')
      setUnit(unitSplit.length === 1 ? defaultUnit : unitSplit[1])
    }
  }, [unit, value, defaultUnit])

  const handleChangeUnit = newUnit => {
    setUnit(newUnit)
    const newValue = `${value.split('/')[0]}${newUnit !== 'unitless' ? `/${newUnit}` : ''}`
    onChange(newValue)
  }

  const handleChangeValue = value => {
    const newValue = `${value}${unit !== 'unitless' ? `/${unit}` : ''}`
    onChange(newValue)
  }

  return <>
    <div className='flex gap-2 w-full'>
      <Input
        type='number'
        value={value.split('/')[0]}
        onChange={e => handleChangeValue(e.target.value)}
      />

      {units.length > 0 && <>
        <Select
          value={unit}
          options={units}
          onChange={option => handleChangeUnit(option.value)}
          placeholder='Select a unit'
        />
      </>}
    </div>
  </>
}

const Toggles = ({ type, onChange, index, config }) => <>
  <ToggleOptionGroup className='w-full' size='lg' value={type} type='single' onValueChange={value => value && onChange(index, value, 'type')}>
    {config.types.map(type => <>
      <ToggleOptionGroupItem key={type} value={type}>
        <DefaultTooltip content={ICONS[type].tooltip}>
          {ICONS[type].icon}
        </DefaultTooltip>
      </ToggleOptionGroupItem>
    </>)}
  </ToggleOptionGroup>
</>

const SliderInput = ({ value, onChange, max, min, step = 1 }) => <>
  <div className='relative w-full'>
    <Slider
      value={value}
      max={max}
      min={min}
      step={step}
      onChange={onChange}
      tooltip={false}
    />
    <small className='absolute left-1/2 -translate-x-1/2 top-full mt-0.5'>
      <label className='mb-0'>
        {value}
      </label>
    </small>
  </div>
</>

const ICONS = {
  x: {
    icon: <ChevronsLeftRight size='14' />,
    tooltip: 'X axis',
  },
  y: {
    icon: <ChevronsUpDown size='14' />,
    tooltip: 'Y axis',
  },
  all: {
    icon: <SquareSquare size='14' />,
    tooltip: 'All axes',
  },
}
